/**
 * YOUR AGREEMENT, at `/account/agreement` (round five, slice C2): what was agreed for each home and
 * service, reached from Account's "Your regular cleans". No artboard in
 * `faithfully-kept-business/design/home/` draws it; it is built from Account's own parts and Home's
 * "What we have written down" card, a label over a value on each row, in the current tokens, and
 * goes on business issue 13's list. The words are `AGREEMENT` in `words.ts`, NEW for the owner to
 * read but where they say SEAM (ops's section 12.1) or WEBSITE (the website's own words, quoted).
 *
 * - "BACK TO ACCOUNT", then "Account" over "Your agreement", whose heading takes the focus on
 *   arrival as every place's does (`components/RouteFocus.tsx`); then the day the household first
 *   booked, "With us since", with its year.
 * - ONE SECTION PER HOME AND SERVICE, in the Worker's order (by home, the newest first), each home
 *   named by its label when the agreements are at several homes: the service in the website's own
 *   words, its state, how often and on which weekday, never an arrival window, a time or a length; a
 *   pause that has not ended, as Account says one; then its rows: the price of one visit with how it
 *   was set, in 12.1's two words alone, a client's alone; the line on the price list, and for a
 *   client that it is a starting price where the page publishes "from"; a home watch season, with
 *   its quoted total for a client; in force from, a planned last day and agreed on, each with its
 *   year; the standing add-ons as a list, which is empty in ops's revision; and when invoices are
 *   due, a client's alone. Under them the day the first visit's deep clean was said, a client's
 *   alone, and the written scope of work when one is pinned, its words as pinned, the office's
 *   number in them a link held whole on its line (`HeldWords`, round six, slice S4).
 * - ENDED IN THE LAST TWELVE MONTHS, under its own heading: each agreement ops sent that has ended,
 *   by the day it ended, with no pause and no scope.
 * - AT SEVERAL HOMES EACH ONE'S DETAILS FOLD (round six, slice S4): the replay of 2026-10-18 found
 *   the page about thirteen phone screens long for four homes. Each card says what she needs first,
 *   its home, its service, its state, how often, a pause and, for a client, the price of one visit;
 *   then "The details", a native button named with the home and the service, saying whether it is
 *   open by `aria-expanded` and its chevron, that draws the rest of the rows, the deep clean said
 *   and the scope under it. One opens at a time, the one open before folding away, and the button
 *   pressed is kept where it was on the screen (`useKeptInPlace`), at once, with no animation. At
 *   one home nothing folds. Nothing else on the page folds.
 * - YOUR AUTOMATIC PAYMENT, a client's alone: the method in ops's words, the day it was agreed and
 *   where, and "The words we hold for your automatic payment" over the words ops holds for it, drawn
 *   as ops sends them, every character, the office's number in them a link held whole on its line,
 *   never split at its hyphen (`HeldWords`, round six, slice S4); or that nothing is saved, as
 *   Payments says it; and the way to How you pay, where Stop is. Her Stop is read as How you pay
 *   reads it (round six, slice S2): sent, then stopped once ops says so, in place of the method, the
 *   words held kept under it. Never who agreed, and never "you agreed": ops records the person a
 *   link was made for, who may not be the person who pressed.
 * - THE PUBLISHED TERMS: for a client the Terms' own two sentences that say the business's one
 *   rule, and the pricing page's sentence on its rates, said as that page's, "The pricing page,
 *   linked below, says:", with the pricing page ops read the price from linked straight under it, so
 *   "the foot of this page" is that page's foot, which prints the date, never this page's (round
 *   six, slice S4); for everyone the current Terms, cancellation and re-clean policy and Privacy,
 *   which is what the links open, as the line under them says.
 * - A MEMBER SEES HER HOMES' AGREEMENTS AND NO MONEY. The Worker gives a member none, and this page
 *   draws money only where the household says she pays here, so a figure sent to a member by
 *   mistake is still never drawn.
 * - ASK FOR A CHANGE, a client's alone (round five, slice C3, ops's section 12.4): under the
 *   agreements in place, each home with an ask to offer or an ask to say, by its name where there
 *   are several, its regular cleans, where the household's asks stand and a button for each ask
 *   the Worker offers, to pause, resume, change or end them, each opening its sheet, as Account
 *   draws them (`components/CleansAsks.tsx`, `CleansSheet.tsx`).
 * - Ops decides (CLAUDE.md binding rule 1): nothing here reads paused, changed or ended until ops
 *   pushes it; an ask reads as sent, then as ops says, and an accepted one arrives as the
 *   agreement, home and visit pushes that make it true; a new cadence's price is the office's to
 *   say, in its message. It is read from GET /api/account/agreements, and read again once an ask is
 *   sent. While the agreements are still coming down, the rest is on its way; a 401 is the door, a
 *   404 the page that says to call, and a read that does not go through says so with the phone.
 */
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  getAgreements,
  type Agreement,
  type AgreementsPlace,
  type AgreementScope,
  type AutopayTerms,
  type HomeCleans,
  type StopAsk,
} from '../api.ts';
import { CARD_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { Chevron, Onward } from '../components/Chevron.tsx';
import { useCleansAsks, type CleansAsking } from '../components/CleansAsks.tsx';
import { FOOTER_LINK } from '../components/Footer.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Shell } from '../components/Shell.tsx';
import { HeldWords } from '../components/Terms.tsx';
import { POLICY_HREF, PRIVACY_HREF, TERMS_HREF } from '../contact.ts';
import { cleansLine } from '../lib/cleans.ts';
import { dateInWords, fullDate } from '../lib/dates.ts';
import { standing } from '../lib/standing.ts';
import { ACCOUNT_PAYING_PATH } from '../paths.ts';
import {
  ACCOUNT,
  AGREEMENT,
  agreedThrough,
  agreementState,
  chargesAutomatically,
  CLEANS,
  deepCleanSaid,
  howOften,
  invoicesDue,
  pausedFrom,
  priceOfOneVisit,
  REQUEST,
  REST_ON_ITS_WAY,
  scopeVersion,
  seasonWords,
  serviceName,
  standingAddon,
  STOP,
} from '../words.ts';
import { AccountPage, EYEBROW } from './Account.tsx';

const CARD = 'rounded-card border border-border-subtle bg-surface-raised p-5';
const CARD_TITLE = 'font-serif text-h3 font-semibold text-text-brand';
const SECTION_HEADING = 'font-serif text-h3 font-semibold text-text-body';
const LINE = 'text-body text-pretty text-text-body';
const QUOTED = 'border-l-4 border-border-subtle pl-4 text-body text-pretty text-text-body';

export function AccountAgreement(signed: Signed) {
  return (
    <Guarded load={getAgreements} signed={signed} at={null} heading={AGREEMENT.heading}>
      {(place, reload) => <AgreementDrawn place={place} signed={signed} reload={reload} />}
    </Guarded>
  );
}

/** What the page says of the household, read once for every section. */
interface Said {
  /** She pays here, so money is drawn: the Worker says so of a client alone. */
  readonly client: boolean;
  /**
   * The agreements are at more than one home, so each is named by its home, and each one's details
   * fold under what she needs first, opened one at a time (round six, slice S4).
   */
  readonly named: boolean;
}

/** An agreement's details folded under what it says first: whether they are open, and the press. */
interface Fold {
  readonly open: boolean;
  readonly onToggle: (button: HTMLButtonElement) => void;
}

function AgreementDrawn({
  place,
  signed,
  reload,
}: {
  readonly place: AgreementsPlace;
  readonly signed: Signed;
  readonly reload: () => void;
}) {
  const said: Said = {
    client: place.household.payments === true,
    named: new Set(place.agreements.map((agreement) => agreement.homeId)).size > 1,
  };
  const live = place.agreements.filter((agreement) => agreement.state !== 'ended');
  const ended = place.agreements.filter((agreement) => agreement.state === 'ended');
  const booked = place.bookedOn === undefined ? null : fullDate(place.bookedOn);
  const cleans = place.cleans ?? [];
  // The one agreement whose details are open, where they fold: one at a time.
  const [opened, setOpened] = useState<string | null>(null);
  const keepInPlace = useKeptInPlace(opened);
  const foldOf = (agreement: Agreement): Fold | null =>
    said.named
      ? {
          open: opened === agreement.id,
          onToggle: (button) => {
            keepInPlace(button);
            setOpened((was) => (was === agreement.id ? null : agreement.id));
          },
        }
      : null;
  const asks = useCleansAsks({
    homes: cleans,
    household: place.household.id,
    today: place.today,
    reload,
    onSignedOut: signed.onSignedOut,
  });
  return (
    <Shell at={null} payments={place.household.payments} over={asks.over}>
      <AccountPage heading={AGREEMENT.heading}>
        {booked === null ? null : (
          <div data-booked>
            <Rows rows={[[AGREEMENT.withUsSince, booked]]} />
          </div>
        )}
      </AccountPage>

      {place.arriving.agreements ? (
        <p className={LINE}>{REST_ON_ITS_WAY}</p>
      ) : live.length === 0 ? (
        <p className={LINE}>{AGREEMENT.noneInPlace}</p>
      ) : (
        live.map((agreement) => (
          <AgreementCard
            key={agreement.id}
            agreement={agreement}
            said={said}
            level={2}
            fold={foldOf(agreement)}
          />
        ))
      )}

      <AskForAChange homes={cleans} asks={asks} />

      {ended.length === 0 ? null : (
        <Section heading={AGREEMENT.endedHeading}>
          {ended.map((agreement) => (
            <AgreementCard
              key={agreement.id}
              agreement={agreement}
              said={said}
              level={3}
              fold={foldOf(agreement)}
            />
          ))}
        </Section>
      )}

      {said.client && 'autopay' in place ? (
        <AutomaticPayment
          autopay={place.autopay ?? null}
          stopAsk={place.stopAsk}
          arriving={place.arriving.household}
        />
      ) : null}

      <PublishedTerms place={place} client={said.client} />
    </Shell>
  );
}

/**
 * Keeps the button she pressed where it was on the screen once the page is drawn again: opening one
 * agreement folds away the one open before, and where that one is above, everything under it would
 * move up by its height, the button and the details she opened with it, out of sight on a phone.
 * Where the browser's own scroll anchoring has already held the button in place, nothing moves; the
 * page is put back at once, with no animation, so it never moves where reduced motion is asked for.
 */
function useKeptInPlace(changed: unknown): (button: HTMLElement) => void {
  const held = useRef<{ readonly button: HTMLElement; readonly top: number } | null>(null);
  useLayoutEffect(() => {
    const was = held.current;
    held.current = null;
    if (was === null || !was.button.isConnected) return;
    const moved = was.button.getBoundingClientRect().top - was.top;
    if (Math.abs(moved) >= 1) window.scrollBy({ top: moved, behavior: 'instant' });
  }, [changed]);
  return (button) => {
    held.current = { button, top: button.getBoundingClientRect().top };
  };
}

/**
 * "Ask for a change", a client's alone (round five, slice C3, ops's section 12.4): each home with an
 * ask to offer or an ask to say, by its name when there are several, its regular cleans as Account
 * says them, and where its asks stand and its buttons, as Account draws them (`CleansAsks.tsx`).
 * Nothing for a member, whom the Worker gives none, and nothing where no home has either.
 */
function AskForAChange({
  homes,
  asks,
}: {
  readonly homes: readonly HomeCleans[];
  readonly asks: CleansAsking;
}) {
  const shown = homes.filter((home) => home.offers.length > 0 || home.requests.length > 0);
  if (shown.length === 0) return null;
  const several = shown.length > 1;
  return (
    <Section heading={CLEANS.heading}>
      {shown.map((home) => (
        <HomeAsks key={home.id} home={home} named={several} asks={asks} />
      ))}
    </Section>
  );
}

function HomeAsks({
  home,
  named,
  asks,
}: {
  readonly home: HomeCleans;
  readonly named: boolean;
  readonly asks: CleansAsking;
}) {
  const labelId = useId();
  const line = cleansLine(home);
  const labelled = named && home.label !== undefined;
  return (
    <div data-cleans-home={home.id} className={CARD}>
      {labelled ? (
        <h3 id={labelId} className={EYEBROW}>
          {home.label}
        </h3>
      ) : null}
      {line === null ? null : <p className={`${labelled ? 'mt-1' : ''} ${LINE}`}>{line}</p>}
      {asks.block(home, labelled ? labelId : undefined)}
    </div>
  );
}

/** A section of the page under its own heading, read by it. */
function Section({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <h2 id={headingId} className={SECTION_HEADING}>
        {heading}
      </h2>
      {children}
    </section>
  );
}

/** Rows of a label over its value, as Home's "What we have written down" draws them. */
function Rows({ rows }: { readonly rows: readonly (readonly [string, ReactNode])[] }) {
  if (rows.length === 0) return null;
  return (
    <dl className="mt-2 flex flex-col">
      {rows.map(([label, value]) => (
        <div key={label} className="border-b border-border-subtle py-3 last:border-b-0">
          <dt className="text-small text-text-muted">{label}</dt>
          <dd className={LINE}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A day with its year, or nothing for one that is not a real day. */
const dayOf = (day: string | undefined): string | null =>
  day === undefined ? null : fullDate(day);

interface CardProps {
  readonly agreement: Agreement;
  readonly said: Said;
  /** The level of its heading: 2 among the agreements in place, 3 under those that ended. */
  readonly level: 2 | 3;
  /** Where its details fold, whether they are open and the press that opens or folds them. */
  readonly fold: Fold | null;
}

/**
 * One agreement: its home when there are several, its service, its state, how often, a pause that
 * has not ended, its rows, the deep clean said, and the written scope while it is in place. Where
 * its details fold (`fold`, at several homes), the price of one visit is said with what comes first,
 * for a client, and the rest of the rows, the deep clean and the scope are drawn under "The
 * details" only while it is open: a native button, so a keyboard opens it with Enter or Space, that
 * says whether it is open by `aria-expanded` and its chevron, names the details it opens
 * (`aria-controls`) and is named with the home and service.
 */
function AgreementCard({ agreement, said, level, fold }: CardProps) {
  const homeId = useId();
  const headingId = useId();
  const wordsId = useId();
  const detailsId = useId();
  const isEnded = agreement.state === 'ended';
  const often = howOften(agreement.cadence, agreement.weekday);
  const pause = isEnded ? null : pauseLine(agreement);
  const state =
    pause !== null && agreement.state === 'paused'
      ? null
      : agreementState(agreement.state, dayOf(agreement.endsOn));
  const named = said.named && agreement.homeLabel !== undefined;
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <section
      data-agreement
      aria-labelledby={named ? `${homeId} ${headingId}` : headingId}
      className={CARD}
    >
      {named ? (
        <p id={homeId} className={EYEBROW}>
          {agreement.homeLabel}
        </p>
      ) : null}
      <Heading id={headingId} className={`${named ? 'mt-1' : ''} ${CARD_TITLE}`}>
        {serviceName(agreement.service)}
      </Heading>
      {state === null ? null : <p className={`mt-1 ${LINE}`}>{state}</p>}
      {often === null ? null : <p className={LINE}>{`${often}.`}</p>}
      {pause === null ? null : <p className={LINE}>{pause}</p>}
      {fold === null ? (
        <Details agreement={agreement} said={said} rows={rowsOf(agreement, said.client)} />
      ) : (
        <>
          <Rows rows={priceRowOf(agreement, said.client)} />
          <button
            type="button"
            aria-expanded={fold.open}
            aria-controls={detailsId}
            aria-labelledby={[wordsId, ...(named ? [homeId] : []), headingId].join(' ')}
            onClick={(event) => fold.onToggle(event.currentTarget)}
            className={`mt-4 gap-2 ${CARD_BUTTON}`}
          >
            <span id={wordsId}>{AGREEMENT.details}</span>
            <Chevron open={fold.open} />
          </button>
          {fold.open ? (
            <div id={detailsId} data-details className="mt-2">
              <Details
                agreement={agreement}
                said={said}
                rows={detailRowsOf(agreement, said.client)}
              />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/** An agreement's rows as given, the deep clean said, and the written scope while it is in place. */
function Details({
  agreement,
  said,
  rows,
}: {
  readonly agreement: Agreement;
  readonly said: Said;
  readonly rows: readonly (readonly [string, ReactNode])[];
}) {
  const deepClean =
    said.client && agreement.deepCleanSaidOn !== undefined
      ? dayOf(agreement.deepCleanSaidOn)
      : null;
  return (
    <>
      <Rows rows={rows} />
      {deepClean === null ? null : (
        <p className="mt-2 text-small text-pretty text-text-body">{deepCleanSaid(deepClean)}</p>
      )}
      {agreement.state === 'ended' || agreement.scope === undefined ? null : (
        <WrittenScope scope={agreement.scope} />
      )}
    </>
  );
}

/** A pause that has not ended, as Account says a home's, with no year; the Worker sends no other. */
function pauseLine(agreement: Agreement): string | null {
  const from = agreement.pausedFrom === undefined ? null : dateInWords(agreement.pausedFrom);
  if (from === null) return null;
  const until = agreement.pausedUntil === undefined ? null : dateInWords(agreement.pausedUntil);
  return pausedFrom(from, until);
}

/** An agreement's rows, money among them only for a client: the price of one visit, then the rest. */
const rowsOf = (agreement: Agreement, client: boolean): [string, ReactNode][] => [
  ...priceRowOf(agreement, client),
  ...detailRowsOf(agreement, client),
];

/** The price of one visit, a client's alone, as its row; none where ops gave none. */
function priceRowOf(agreement: Agreement, client: boolean): [string, ReactNode][] {
  return client && agreement.priceCents !== undefined
    ? [[AGREEMENT.priceOfOneVisit, priceOfOneVisit(agreement.priceCents, agreement.priced)]]
    : [];
}

/** An agreement's rows after its price, money among them only for a client. */
function detailRowsOf(agreement: Agreement, client: boolean): [string, ReactNode][] {
  const rows: [string, ReactNode][] = [];
  if (agreement.line !== undefined) {
    rows.push([
      AGREEMENT.line,
      <>
        {agreement.line}
        {client && agreement.publishedFrom ? (
          <span className="block text-small text-pretty text-text-muted">
            {' '}
            {AGREEMENT.startingPrice}
          </span>
        ) : null}
      </>,
    ]);
  }
  const season =
    agreement.season === undefined
      ? null
      : seasonWords(agreement.season.visits, client ? agreement.season.totalCents : undefined);
  if (season !== null) rows.push([AGREEMENT.season, season]);
  const startsOn = dayOf(agreement.startsOn);
  if (startsOn !== null) rows.push([AGREEMENT.inForceFrom, startsOn]);
  const lastDay = agreement.state === 'ended' ? null : dayOf(agreement.endsOn);
  if (lastDay !== null) rows.push([AGREEMENT.lastDay, lastDay]);
  const agreedOn = dayOf(agreement.agreedOn);
  if (agreedOn !== null) rows.push([AGREEMENT.agreedOn, agreedOn]);
  rows.push([
    AGREEMENT.addedToEachVisit,
    <AddOns key="addons" agreement={agreement} client={client} />,
  ]);
  if (client && agreement.invoiceTermsDays !== undefined) {
    rows.push([AGREEMENT.invoices, invoicesDue(agreement.invoiceTermsDays)]);
  }
  return rows;
}

/** The standing add-ons as a list, each with its price for a client; "Nothing" with none. */
function AddOns({
  agreement,
  client,
}: {
  readonly agreement: Agreement;
  readonly client: boolean;
}) {
  if (agreement.addons.length === 0) return <>{AGREEMENT.nothingAdded}</>;
  return (
    <ul className="flex list-disc flex-col gap-1 pl-6">
      {agreement.addons.map((addon, index) => (
        <li key={`${addon.label}-${index}`}>
          {standingAddon(addon.label, addon.quantity, client ? addon.unitPriceCents : undefined)}
        </li>
      ))}
    </ul>
  );
}

/** The written scope the agreement pins: which one it is, and its words as she was shown them. */
function WrittenScope({ scope }: { readonly scope: AgreementScope }) {
  const which = scopeVersion(
    scope.version,
    scope.effectiveFrom === undefined ? null : fullDate(scope.effectiveFrom),
  );
  return (
    <div data-scope className="mt-4 flex flex-col gap-2 border-t border-border-subtle pt-4">
      <h3 className="text-body font-semibold text-text-body">{AGREEMENT.scope}</h3>
      {which === null ? null : <p className="text-small text-text-muted">{which}</p>}
      {scope.body === undefined ? (
        <p className="text-small text-pretty text-text-body">
          {AGREEMENT.scopeBeforePhone} <Phone after="." />
        </p>
      ) : (
        <p className={`${LINE} whitespace-pre-line`}>
          <HeldWords words={scope.body} />
        </p>
      )}
    </div>
  );
}

/**
 * The household's saved method, a client's alone: the method in ops's words, the day it was
 * agreed, where, and the words ops holds for it; or that nothing is saved; and the way to How you
 * pay. While the household is still coming down, nothing says which. Her newest Stop of it is read
 * as Account and How you pay read it (`lib/standing.ts`, round six, slice S2): the method with
 * "Sent to the office." while it is on its way, and "Stopped." or nothing saved once ops says so, in
 * place of the method, before ops's household push comes; the words she agreed under stay under
 * it, the record of what was agreed.
 */
function AutomaticPayment({
  autopay,
  stopAsk,
  arriving,
}: {
  readonly autopay: AutopayTerms | null;
  readonly stopAsk: StopAsk | undefined;
  readonly arriving: boolean;
}) {
  return (
    <Section heading={AGREEMENT.automaticPayment}>
      {arriving ? (
        <p className={LINE}>{REST_ON_ITS_WAY}</p>
      ) : autopay === null ? (
        <p className={LINE}>{STOP.nothingSaved}</p>
      ) : (
        <SavedMethodTerms autopay={autopay} stopAsk={stopAsk} />
      )}
      {arriving ? null : (
        <Link to={ACCOUNT_PAYING_PATH} className={`gap-2 text-body ${TEXT_LINK}`}>
          {ACCOUNT.howYouPay}
          <Onward />
        </Link>
      )}
    </Section>
  );
}

/**
 * What the method's card says first, as Account's How you pay row says it (`payingLines`): the
 * method, with "Sent to the office." while her Stop is on its way; or, once ops has answered it,
 * stopped or nothing saved, in place of the method.
 */
function methodLines(autopay: AutopayTerms, stopAsk: StopAsk | undefined): string[] {
  const method = chargesAutomatically(autopay.label, autopay.methodType, null);
  const said = standing({ autopay: { ...autopay }, stopAsk }, null, false);
  switch (said?.kind) {
    case 'sent':
      return [method, REQUEST.sent];
    case 'stopped':
      return [STOP.stopped];
    case 'nothing_saved':
      return [STOP.nothingSaved];
    default:
      return [method];
  }
}

function SavedMethodTerms({
  autopay,
  stopAsk,
}: {
  readonly autopay: AutopayTerms;
  readonly stopAsk: StopAsk | undefined;
}) {
  const agreedOn = dayOf(autopay.agreedOn);
  const where = agreedThrough(autopay.agreedThrough);
  const [first, ...more] = methodLines(autopay, stopAsk);
  return (
    <div className={CARD}>
      <p className={`font-semibold ${LINE}`}>{first}</p>
      {more.map((line) => (
        <p key={line} className={LINE}>
          {line}
        </p>
      ))}
      <Rows rows={agreedOn === null ? [] : [[AGREEMENT.agreedOn, agreedOn]]} />
      {where === null ? null : <p className={`mt-1 ${LINE}`}>{where}</p>}
      <h3 className="mt-4 text-body font-semibold text-text-body">{AGREEMENT.wordsWeHold}</h3>
      {autopay.words === undefined ? (
        <p className="mt-2 text-small text-pretty text-text-body">
          {AGREEMENT.noWordsBeforePhone} <Phone after="." />
        </p>
      ) : (
        <blockquote className={`mt-2 ${QUOTED} whitespace-pre-line`}>
          <HeldWords words={autopay.words} />
        </blockquote>
      )}
    </div>
  );
}

/**
 * The website's terms: for a client the Terms' two sentences of the business's one rule, and the
 * pricing page's sentence beside each pricing page ops read a price from; for everyone the current
 * Terms, policy and Privacy.
 */
function PublishedTerms({
  place,
  client,
}: {
  readonly place: AgreementsPlace;
  readonly client: boolean;
}) {
  const pricing = client
    ? [
        ...new Set(
          place.agreements.flatMap((agreement) =>
            agreement.pricingUrl === undefined ? [] : [agreement.pricingUrl],
          ),
        ),
      ]
    : [];
  const pages: readonly [string, string][] = [
    [ACCOUNT.terms, TERMS_HREF],
    [ACCOUNT.policy, POLICY_HREF],
    [ACCOUNT.privacy, PRIVACY_HREF],
  ];
  return (
    <Section heading={AGREEMENT.publishedTerms}>
      {client ? (
        <>
          <p className={LINE}>{AGREEMENT.ourTermsSay}</p>
          <blockquote className={`${QUOTED} flex flex-col gap-1`}>
            <p>{AGREEMENT.noSignupFee}</p> <p>{AGREEMENT.eitherSideCanStop}</p>
          </blockquote>
        </>
      ) : null}
      {pricing.length === 0 ? null : (
        <>
          <p className={LINE}>{AGREEMENT.pricingPageSays}</p>
          <blockquote className={QUOTED}>
            <p>{AGREEMENT.ratesReviewed}</p>
          </blockquote>
          {pricing.map((url) => (
            <a key={url} href={url} className={`text-body ${TEXT_LINK}`}>
              {AGREEMENT.pricing}
            </a>
          ))}
        </>
      )}
      <ul className="flex flex-wrap gap-x-6">
        {pages.map(([words, href]) => (
          <li key={href}>
            <a href={href} className={FOOTER_LINK}>
              {words}
            </a>
          </li>
        ))}
      </ul>
      <p className="text-small text-pretty text-text-muted">{AGREEMENT.asItReadsToday}</p>
    </Section>
  );
}
